import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { logError } from "../lib/logError";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { Checkbox } from "./ui/checkbox";
import { CaretRight, CaretDown, Sparkle, FloppyDisk } from "@phosphor-icons/react";

const LEVELS = [["high", "High"], ["medium", "Medium"], ["low", "Low"]];

function ImpactPicker({ value, onChange, testid }) {
  const v = value || [];
  const toggle = (lvl) => onChange(v.includes(lvl) ? v.filter((x) => x !== lvl) : [...v, lvl]);
  return (
    <div className="flex gap-2 shrink-0">
      {LEVELS.map(([lvl, label]) => (
        <label key={lvl} className="flex items-center gap-1 text-xs cursor-pointer">
          <Checkbox data-testid={testid ? `${testid}-${lvl}` : undefined} checked={v.includes(lvl)} onCheckedChange={() => toggle(lvl)} className="rounded-none h-4 w-4" />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
}

export default function CipImpact() {
  const { aiEnabled, aiConfidentialBlocked } = useAuth();
  const aiOff = !aiEnabled || aiConfidentialBlocked;
  const aiOffTitle = !aiEnabled
    ? "AI features are turned off for this entity"
    : aiConfidentialBlocked
    ? "Blocked while AI is set to Public-information only — CIP impact tagging works with your asset data"
    : undefined;
  const [standards, setStandards] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState("");

  useEffect(() => {
    api.get("/standards").then((r) =>
      setStandards(r.data.filter((s) => s.standard_id.toUpperCase().startsWith("CIP-"))
        .sort((a, b) => a.standard_id.localeCompare(b.standard_id, undefined, { numeric: true }))));
  }, []);

  const loadReqs = async (sid) => {
    const { data } = await api.get(`/requirements?standard_id=${sid}`);
    setReqs(data.sort((a, b) => a.req_number.localeCompare(b.req_number, undefined, { numeric: true })));
  };

  const openStd = async (sid) => {
    if (expanded === sid) { setExpanded(null); return; }
    setExpanded(sid); setReqs([]);
    await loadReqs(sid);
  };

  const tagOne = async (sid) => {
    setBusy(`tag-${sid}`);
    try {
      const { data } = await api.post(`/impact/ai-tag/${sid}`);
      toast.success(`AI-tagged ${data.tagged} requirements in ${sid}`);
      if (expanded === sid) await loadReqs(sid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const tagAll = async () => {
    setBusy("tag-all");
    let n = 0, ok = 0;
    for (const s of standards) {
      setProgress(`Tagging ${s.standard_id}… (${++n}/${standards.length})`);
      try { await api.post(`/impact/ai-tag/${s.standard_id}`); ok++; } catch (e) { logError(`ai-tag ${s.standard_id}`, e); }
    }
    setProgress(""); setBusy("");
    toast.success(`AI impact tagging complete: ${ok}/${standards.length} CIP standards`);
    if (expanded) await loadReqs(expanded);
  };

  const setReqImpact = (id, levels) => setReqs((rs) => rs.map((r) => (r.id === id ? { ...r, impact_levels: levels } : r)));
  const setPartImpact = (id, label, levels) => setReqs((rs) => rs.map((r) => (r.id === id
    ? { ...r, sub_requirements: (r.sub_requirements || []).map((p) => (p.label === label ? { ...p, impact_levels: levels } : p)) } : r)));

  const saveReq = async (r) => {
    setBusy(`save-${r.id}`);
    try {
      const parts = {};
      (r.sub_requirements || []).forEach((p) => { parts[p.label] = p.impact_levels || []; });
      await api.patch(`/requirements/${r.id}/impact`, { impact_levels: r.impact_levels || [], parts });
      toast.success(`Saved impact ratings for ${r.req_number}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="cip-impact-panel">
      <div className="border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display font-bold">CIP Impact Rating Tagging</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">AI-tags each CIP requirement &amp; sub-part with the impact rating(s) it addresses (High / Medium / Low). Review and override below. An empty selection means "applies to all ratings" (e.g., CIP-002). This drives applicability &amp; policy generation against the entity's declared impact ratings.</p>
          </div>
          <Button data-testid="tag-all-impact-btn" onClick={tagAll} disabled={!!busy || aiOff} title={aiOffTitle} className="rounded-none bg-primary hover:bg-primary/90 shrink-0"><Sparkle size={16} className="mr-1" />{busy === "tag-all" ? "Tagging…" : "Auto-tag all CIP (AI)"}</Button>
        </div>
        {progress && <div className="mt-3 text-sm font-mono text-muted-foreground" data-testid="tag-progress">{progress}</div>}
      </div>

      <div className="border border-border bg-card divide-y divide-border">
        {standards.length === 0 && <p className="text-sm text-muted-foreground p-4">No CIP standards found. Import standards first.</p>}
        {standards.map((s) => (
          <div key={s.standard_id}>
            <div className="flex items-center justify-between px-4 py-3 gap-3">
              <button data-testid={`impact-std-${s.standard_id}`} onClick={() => openStd(s.standard_id)} className="flex items-center gap-2 text-left min-w-0">
                {expanded === s.standard_id ? <CaretDown size={14} /> : <CaretRight size={14} />}
                <span className="font-mono font-bold text-sm shrink-0">{s.standard_id}</span>
                <span className="text-sm text-muted-foreground truncate">{s.title}</span>
              </button>
              <Button data-testid={`tag-one-${s.standard_id}`} onClick={() => tagOne(s.standard_id)} disabled={!!busy || aiOff} title={aiOffTitle} variant="outline" size="sm" className="rounded-none shrink-0"><Sparkle size={13} className="mr-1" />{busy === `tag-${s.standard_id}` ? "…" : "AI tag"}</Button>
            </div>
            {expanded === s.standard_id && (
              <div className="px-4 pb-4 space-y-3 bg-secondary/20">
                {reqs.length === 0 ? <p className="text-sm text-muted-foreground py-2">Loading…</p> : reqs.map((r) => (
                  <div key={r.id} className="border border-border bg-card p-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-display font-bold text-sm">{r.req_number}</div>
                      <div className="flex items-center gap-3">
                        <ImpactPicker value={r.impact_levels} onChange={(lv) => setReqImpact(r.id, lv)} testid={`req-impact-${r.req_number}`} />
                        <Button data-testid={`save-impact-${r.req_number}`} onClick={() => saveReq(r)} disabled={busy} size="sm" className="rounded-none bg-primary hover:bg-primary/90 h-7"><FloppyDisk size={13} className="mr-1" />Save</Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.text}</p>
                    {(r.sub_requirements || []).length > 0 && (
                      <div className="mt-2 space-y-1.5 border-t border-border pt-2">
                        {r.sub_requirements.map((p) => (
                          <div key={p.label} className="flex items-center justify-between gap-3">
                            <span className="text-xs min-w-0 truncate"><b className="font-mono">{p.label}</b> <span className="text-muted-foreground">{(p.text || "").slice(0, 70)}</span></span>
                            <ImpactPicker value={p.impact_levels} onChange={(lv) => setPartImpact(r.id, p.label, lv)} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
